// contentProvider.ts — the swappable content layer.
//
// Today it returns the local TypeScript programme. The async signature is
// deliberate: when content moves to Supabase / Sanity / Strapi / a CMS, only
// this file changes — screens call `contentProvider.getProgramme()` and the
// selectors below, which are pure and source-agnostic.

import type {
  Programme,
  Module,
  Step,
  ModuleStatus,
  StepStatus,
  SupportingContent,
  CourseRef,
} from '@/types';
import type { ProgressState } from './progress';
import { REAL_PROGRAMME } from '@/data/realProgramme';
import { canOpen } from './compassionDaily';

export const contentProvider = {
  /** Fetch the ordered programme. Async so a remote source can drop in later. */
  async getProgramme(): Promise<Programme> {
    return REAL_PROGRAMME;
  },
};

// ─────────────────────────── pure selectors ───────────────────────────

export function allSteps(programme: Programme): Step[] {
  return programme.modules.flatMap((m) => m.steps);
}

export function findStep(programme: Programme, stepId: string): Step | undefined {
  return allSteps(programme).find((s) => s.id === stepId);
}

export function findModule(programme: Programme, moduleId: string): Module | undefined {
  return programme.modules.find((m) => m.id === moduleId);
}

export function moduleOfStep(programme: Programme, stepId: string): Module | undefined {
  return programme.modules.find((m) => m.steps.some((s) => s.id === stepId));
}

/** The module containing the current step (falls back to the first incomplete module). */
export function currentModule(programme: Programme, progress: ProgressState): Module {
  // Only trust the saved pointer while it still points at unfinished work. A
  // *completed* currentStepId — left behind after revisiting an earlier lesson,
  // or lagging at a course boundary — must not pin "current" to an already-
  // finished module, or the next ACT never opens once the previous one is done.
  if (progress.currentStepId && !progress.completedSteps.includes(progress.currentStepId)) {
    const mod = moduleOfStep(programme, progress.currentStepId);
    if (mod) return mod;
  }
  const firstIncomplete = programme.modules.find(
    (m) => !progress.completedModules.includes(m.id),
  );
  return firstIncomplete ?? programme.modules[programme.modules.length - 1];
}

/** The next meaningful step to resume or begin. */
export function currentStep(programme: Programme, progress: ProgressState): Step {
  // As above: a finished pointer means "resume" should advance to the next
  // unfinished step, not re-open the lesson the user just completed.
  if (progress.currentStepId && !progress.completedSteps.includes(progress.currentStepId)) {
    const step = findStep(programme, progress.currentStepId);
    if (step) return step;
  }
  const mod = currentModule(programme, progress);
  const next = mod.steps.find((s) => !progress.completedSteps.includes(s.id));
  return next ?? mod.steps[0];
}

/** The step immediately after the given one, in programme order (or null at the end). */
export function nextStepAfter(programme: Programme, stepId: string): Step | null {
  const steps = allSteps(programme);
  const i = steps.findIndex((s) => s.id === stepId);
  return i >= 0 && i < steps.length - 1 ? steps[i + 1] : null;
}

export function moduleStatus(
  programme: Programme,
  module: Module,
  progress: ProgressState,
): ModuleStatus {
  if (progress.completedModules.includes(module.id)) return 'done';
  const cur = currentModule(programme, progress);
  if (module.id === cur.id) return 'current';
  // Open if every earlier module is complete; otherwise it opens later.
  const earlier = programme.modules.filter((m) => m.order < module.order);
  const allEarlierDone = earlier.every((m) => progress.completedModules.includes(m.id));
  return allEarlierDone ? 'open' : 'soon';
}

export function stepStatus(
  programme: Programme,
  step: Step,
  progress: ProgressState,
): StepStatus {
  if (progress.completedSteps.includes(step.id)) return 'done';
  if (progress.currentStepId === step.id) return 'current';
  const mod = moduleOfStep(programme, step.id);
  if (mod && moduleStatus(programme, mod, progress) === 'soon') return 'locked';
  return 'open';
}

export function moduleProgress(module: Module, progress: ProgressState): {
  done: number;
  total: number;
  pct: number;
} {
  const total = module.steps.length;
  const done = module.steps.filter((s) => progress.completedSteps.includes(s.id)).length;
  return { done, total, pct: total ? done / total : 0 };
}

export function overallProgress(programme: Programme, progress: ProgressState): {
  done: number;
  total: number;
  pct: number;
} {
  const steps = allSteps(programme);
  const done = steps.filter((s) => progress.completedSteps.includes(s.id)).length;
  return { done, total: steps.length, pct: steps.length ? done / steps.length : 0 };
}

/**
 * Contextual support beside a module: the article/reflection steps in the
 * module, plus any explicit SupportingContent attached to its steps.
 */
export function contextualFor(module: Module): Array<Step | SupportingContent> {
  const supportSteps = module.steps.filter(
    (s) => s.type === 'article' || s.type === 'reflection',
  );
  const attached = module.steps.flatMap((s) => s.supporting_content ?? []);
  return [...supportSteps, ...attached].slice(0, 3);
}

/**
 * The self-advancing feed: the CURRENT module's still-to-do steps, in order.
 * The first is the active one; the rest are locked (must be done in turn). When
 * the module's last step completes, the current module advances and this
 * returns the next module's steps.
 */
export function feedSteps(programme: Programme, progress: ProgressState): Step[] {
  const mod = currentModule(programme, progress);
  return mod.steps.filter((s) => !progress.completedSteps.includes(s.id));
}

/** The next not-yet-completed step in a module (the active one), or null. */
export function firstIncompleteStep(module: Module, progress: ProgressState): Step | null {
  return module.steps.find((s) => !progress.completedSteps.includes(s.id)) ?? null;
}

/**
 * Whether a step may be opened: its module must be unlocked, and the step must
 * be either already completed (revisit) or the next one due in its module —
 * enforcing strictly in-order progress.
 */
export function isStepOpenable(programme: Programme, step: Step, progress: ProgressState): boolean {
  const mod = moduleOfStep(programme, step.id);
  if (!mod) return false;
  if (moduleStatus(programme, mod, progress) === 'soon') return false;
  if (progress.completedSteps.includes(step.id)) return true;
  if (firstIncompleteStep(mod, progress)?.id !== step.id) return false;
  // Self-compassion steps also obey the one-exercise-per-day gate (canOpen is the single source).
  if (step.id.startsWith('sc-')) return canOpen(programme, step, progress, !!progress.devMode);
  return true;
}

// ─────────────────────── trial content cap (Sign-Up) ───────────────────────
// A free (non-purchased) user may work up to and INCLUDING this step; everything
// after it is gated behind the Sign-Up wall. This is an amount cap, separate from
// the 10-day time gate. Enforced only when activation is enabled and the user
// hasn't purchased — see App.tsx. Hidden entirely for purchased users.
export const FREE_CAP_STEP_ID = 'act1-c04-l06';

/** Post-trial cap: the last step of Act 1 chapter 3 ("1.1 Why we rarely think it's damaging
 *  to us", act1-c03). REAL_PROGRAMME sorts steps by `order`, so the last one is act1-c03-l17
 *  "Your Progress" (order 17; act1-c03-l08-r sits at 7.5). Capping here keeps all of 1.1 open
 *  and locks from the Key Skill chapter (act1-c04) on. Once the 10-day trial has lapsed, a
 *  non-member may work up to and including this step. */
export const POST_TRIAL_CAP_STEP_ID = 'act1-c03-l17';

/** Which step is the last open one for this user, or null for no cap.
 *  member / activation off / no session -> null; in trial -> FREE_CAP_STEP_ID;
 *  trial over (featuresLocked) -> POST_TRIAL_CAP_STEP_ID (end of 1.1). */
export function freeCapStepId(auth: { enabled: boolean; isMember: boolean; featuresLocked: boolean }): string | null {
  if (!auth.enabled || auth.isMember) return null;
  return auth.featuresLocked ? POST_TRIAL_CAP_STEP_ID : FREE_CAP_STEP_ID;
}

/** True if `stepId` falls strictly after the cap step in this programme's order
 *  (`capId` defaults to the in-trial cap). False if the cap step isn't in this
 *  programme (so the self-compassion track, which has none, is never gated). */
export function isBeyondFreeCap(programme: Programme, stepId: string, capId: string | null = FREE_CAP_STEP_ID): boolean {
  if (!capId) return false;
  const steps = allSteps(programme);
  const cap = steps.findIndex((s) => s.id === capId);
  if (cap < 0) return false;
  return steps.findIndex((s) => s.id === stepId) > cap;
}

/** Module display name from its title parts.
 *  Most modules carry a single part (the descriptive title). The self-compassion
 *  course splits its weekly modules into a "Week N" prefix + a topic em, so when
 *  both are present render them together as "Week 1 · Inviting self-compassion". */
export function moduleName(module: Module): string {
  const pre = (module.title.pre || '').trim();
  const em = (module.title.em || '').trim();
  const post = (module.title.post || '').trim();
  if (pre && em) return `${pre} · ${em}`;
  return em || post || pre;
}

// ─────────────────────────── courses (pillars) ───────────────────────────

export function courses(programme: Programme): CourseRef[] {
  return [...(programme.courses ?? [])].sort((a, b) => a.order - b.order);
}

export function courseOfModule(programme: Programme, moduleId: string): CourseRef | undefined {
  return programme.courses?.find((c) => c.moduleIds.includes(moduleId));
}

/** Is this module the INTRO (first) or SUMMARY (last) module of its act? Those
 *  bookend modules are excluded from the end-of-chapter feedback prompt so the
 *  micro-survey is asked consistently on the act's content chapters only (an
 *  intro/summary chapter isn't really "content" to rate). Positional: first and
 *  last entry of the course's ordered moduleIds. */
export function isIntroOrSummaryModule(programme: Programme, moduleId: string): boolean {
  const ids = courseOfModule(programme, moduleId)?.moduleIds ?? [];
  if (ids.length === 0) return false;
  return moduleId === ids[0] || moduleId === ids[ids.length - 1];
}

/** Locate a chapter within its act for human-readable labelling. Chapter numbers
 *  are POSITIONAL within the act (contiguous, so deleted modules don't leave gaps)
 *  — not module.order. Used so labels read e.g. "Self-Compassion & Self-Worth ·
 *  Chapter 2" instead of a bare "Chapter 1" that makes no sense deep in Act 3. */
export function chapterLocator(
  programme: Programme,
  moduleId: string,
): { actName: string; chapterNo: number; chapterCount: number } {
  const course = courseOfModule(programme, moduleId);
  const ids = course?.moduleIds ?? [];
  const idx = ids.indexOf(moduleId);
  return { actName: course?.title ?? '', chapterNo: idx >= 0 ? idx + 1 : 1, chapterCount: ids.length };
}

/** The course containing the current module. */
export function currentCourse(programme: Programme, progress: ProgressState): CourseRef | undefined {
  const mod = currentModule(programme, progress);
  return courseOfModule(programme, mod.id);
}

export type CourseStatus = 'done' | 'current' | 'soon';
export function courseStatus(
  programme: Programme,
  course: CourseRef,
  progress: ProgressState,
): CourseStatus {
  const allDone = course.moduleIds.every((id) => progress.completedModules.includes(id));
  if (allDone) return 'done';
  const cur = currentCourse(programme, progress);
  return cur && cur.id === course.id ? 'current' : 'soon';
}

export function modulesOfCourse(programme: Programme, course: CourseRef): Module[] {
  return course.moduleIds
    .map((id) => programme.modules.find((m) => m.id === id))
    .filter((m): m is Module => !!m);
}

export function courseProgress(
  course: CourseRef,
  programme: Programme,
  progress: ProgressState,
): { done: number; total: number } {
  const mods = modulesOfCourse(programme, course);
  const done = mods.filter((m) => progress.completedModules.includes(m.id)).length;
  return { done, total: mods.length };
}
